import { GlobalNavigationMenu } from '@diritto/common/navigation/constant';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { DesktopGlobalMenuWidth, isPC } from 'constants/layout';
import {
  GlobalNavigationMenuHref,
  GlobalNavigationMenuText,
  NewGlobalNavigationMenuDisplayList,
  NewPartnerGlobalNavigationMenuDisplayList,
} from 'constants/navigation';
import { HomeIcon } from 'icons/navigation/HomeIcon';
import { LatestIcon } from 'icons/navigation/LatestIcon';
import { MyIcon } from 'icons/navigation/MyIcon';
import { QuestIcon } from 'icons/navigation/QuestIcon';
import { RankingIcon } from 'icons/navigation/RankingIcon';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'ui-components/common/Link';
import { getDynamicGlobalNavHref } from 'utils/getDynamicGlobalNavHref';
import { ExploreIcon } from 'icons/navigation/ExploreIcon';
import { useAuthState } from 'contexts/AuthProvider';
import { UserType } from '@diritto/common/account/constants/user.constant';

interface MenuProps {
  className?: string;
  selectedMenu?: GlobalNavigationMenu;
}

const IconMap = {
  [GlobalNavigationMenu.Home]: HomeIcon,
  [GlobalNavigationMenu.Explore]: ExploreIcon,
  [GlobalNavigationMenu.Latest]: LatestIcon,
  [GlobalNavigationMenu.Ranking]: RankingIcon,
  [GlobalNavigationMenu.My]: MyIcon,
  [GlobalNavigationMenu.Quest]: QuestIcon,
};

export const DesktopGlobalMenu = ({ className, selectedMenu }: MenuProps) => {
  const pathname = usePathname();
  const [hrefMap, setHrefMap] = useState(GlobalNavigationMenuHref);
  const { user } = useAuthState();
  let displayList;
  if (user?.type === UserType.Partner) {
    displayList = NewPartnerGlobalNavigationMenuDisplayList;
  } else {
    displayList = NewGlobalNavigationMenuDisplayList;
  }

  useEffect(() => {
    // React Hydration Error 를 피하기 위해 effect 에서 처리
    const map = NewGlobalNavigationMenuDisplayList.reduce(
      (acc, globalMenu) => ({ ...acc, [globalMenu]: getDynamicGlobalNavHref(globalMenu) }),
      {},
    ) as typeof GlobalNavigationMenuHref;
    setHrefMap(map);
  }, [displayList, pathname]);

  const renderIcon = useCallback(
    (key: GlobalNavigationMenu) => {
      const Icon = IconMap[key] ?? HomeIcon;
      return (
        <Icon
          fill={selectedMenu === key}
          color={selectedMenu === key ? Color.Primary500 : Color.Gray500}
          className="size-[26px]"
        />
      );
    },
    [selectedMenu],
  );

  return (
    <StyledMenu className={className}>
      {displayList.map(key => (
        <Link key={key} href={hrefMap[key]}>
          <MenuTextWrapper isSelected={selectedMenu === key}>
            {renderIcon(key)}
            <MenuText isSelected={selectedMenu === key}>{GlobalNavigationMenuText[key]}</MenuText>
          </MenuTextWrapper>
        </Link>
      ))}
    </StyledMenu>
  );
};

const StyledMenu = styled.nav`
  display: none;
  width: ${DesktopGlobalMenuWidth}px;
  flex-direction: column;
  padding-right: 24px;
  padding-top: 16px;

  @media (${isPC}) {
    display: flex;
    justify-content: initial;
    height: 100%;
  }
`;

const MenuTextWrapper = styled.div(
  ({ isSelected }: { isSelected?: boolean }) => css`
    display: flex;
    width: initial;
    padding: 11px 16px;
    background-color: ${isSelected ? Color.Primary100 : 'initial'};
    border-radius: 4px;
  `,
);

const MenuText = styled.p(
  ({ isSelected }: { isSelected?: boolean }) => css`
    height: 100%;
    padding: 0 8px;
    display: flex;
    cursor: pointer;
    color: ${isSelected ? Color.Primary500 : Color.Gray500};
    font-weight: ${FontWeight.Bold};
    ${Typography.XXSmall}
    line-height: 26px;
  `,
);
